/*
  Zibby Footer: wordmark seam measurement.
  --------------------------------------------------------------------------
  Two jobs, both guarded so a failure anywhere just leaves the CSS fallback
  in place (a presentable Solid mark, sized from the footer's own width):

  1. measureMark() finds where the two bowls of the B join by rasterising a
     capital B and looking for the row with the most continuous ink. That
     seam becomes the colour boundary (--seam-px) and sets the zone height
     (--mark-h) and mark width (--mark-w) from the real rendered letters,
     whatever font ends up loading. It also lifts the link columns up to sit
     just below the rule when they are clear of the wordmark horizontally.

  2. The mark treatment (Solid, or the opt-in Split) is decided server-side
     in the section's Liquid; this file never changes it.
*/
(function () {
  'use strict';

  var root = document.getElementById('ZibbyFooter');
  if (!root) return;

  function px(n) {
    return n.toFixed(1) + 'px';
  }

  var probe = null;
  try {
    probe = document.createElement('canvas').getContext('2d');
  } catch (e) {
    probe = null;
  }

  var waistCache = {};

  /* Where the two bowls of the B join, as a fraction of its cap height.
     Measured rather than assumed, because the waist of a serif capital
     sits above the geometric centre and the exact spot depends on the
     font that actually loaded. */
  function waistFraction(fontShorthand) {
    if (!probe) return 0.5;
    if (waistCache[fontShorthand] !== undefined) return waistCache[fontShorthand];

    try {
      var size = 200;
      var canvas = document.createElement('canvas');
      canvas.width = 240;
      canvas.height = 320;
      var ctx = canvas.getContext('2d');
      if (!ctx) return (waistCache[fontShorthand] = 0.5);

      ctx.font = fontShorthand.replace(/\d+(\.\d+)?px/, size + 'px');
      ctx.textBaseline = 'alphabetic';
      ctx.fillStyle = '#000';
      var baseline = 260;
      ctx.fillText('B', 12, baseline);

      var m = ctx.measureText('B');
      var inkTop = baseline - m.actualBoundingBoxAscent;
      var capH = m.actualBoundingBoxAscent + m.actualBoundingBoxDescent;
      if (!capH) return (waistCache[fontShorthand] = 0.5);

      var pixels = ctx.getImageData(0, 0, canvas.width, canvas.height).data;
      var best = null;
      var rows = [];

      for (var y = Math.floor(inkTop); y < Math.ceil(inkTop + capH); y++) {
        var frac = (y - inkTop) / capH;
        if (frac < 0.3 || frac > 0.72) continue;
        var count = 0, first = -1, last = -1;
        for (var x = 0; x < canvas.width; x++) {
          if (pixels[(y * canvas.width + x) * 4 + 3] > 128) {
            count++;
            if (first < 0) first = x;
            last = x;
          }
        }
        var span = last - first + 1;
        var fill = span > 0 ? count / span : 0;
        rows.push({ frac: frac, fill: fill });
        if (!best || fill > best.fill) best = { frac: frac, fill: fill };
      }

      if (!best) return (waistCache[fontShorthand] = 0.5);

      var band = rows.filter(function (r) { return r.fill > best.fill - 0.02; });
      var avg = band.reduce(function (a, r) { return a + r.frac; }, 0) / band.length;
      return (waistCache[fontShorthand] = avg || 0.5);
    } catch (e) {
      return (waistCache[fontShorthand] = 0.5);
    }
  }

  /* Where the ink actually starts and ends inside the mark element, and
     where the B's waist falls within it. */
  function inkExtents(mark) {
    if (!probe) return null;
    try {
      var cs = getComputedStyle(mark);
      probe.font = cs.fontStyle + ' ' + cs.fontWeight + ' ' + cs.fontSize + ' ' + cs.fontFamily;
      var m = probe.measureText('ZIBBY');
      if (!m || typeof m.actualBoundingBoxAscent !== 'number') return null;

      var lineHeight = parseFloat(cs.lineHeight);
      if (!lineHeight) return null;
      var halfLeading = (lineHeight - (m.fontBoundingBoxAscent + m.fontBoundingBoxDescent)) / 2;
      var baselineY = halfLeading + m.fontBoundingBoxAscent;

      var inkTop = baselineY - m.actualBoundingBoxAscent;
      var capHeight = m.actualBoundingBoxAscent + m.actualBoundingBoxDescent;
      if (!(capHeight > 0)) return null;

      return {
        top: inkTop,
        capHeight: capHeight,
        seam: inkTop + capHeight * waistFraction(probe.font)
      };
    } catch (e) {
      return null;
    }
  }

  function measureMark() {
    try {
      var mark = root.querySelector('.mark--below');
      if (!mark) return;

      var markRect = mark.getBoundingClientRect();
      if (!markRect || !markRect.width) return;
      root.style.setProperty('--mark-w', px(markRect.width));

      var ink = inkExtents(mark);
      if (!ink) return;

      root.style.setProperty('--seam-px', px(ink.seam));

      /* the cropped line box lets the ink overshoot the element's top edge
         (ink.top goes negative); in Solid the letters sink until the air
         above the ink equals the left inset (--mark-top) */
      var topValue = parseFloat(getComputedStyle(root).getPropertyValue('--mark-top'));
      var airTarget = isNaN(topValue) ? 46 : topValue;
      var sink = Math.max(0, airTarget - ink.top);
      root.style.setProperty('--mark-sink', px(sink));

      var padValue = parseFloat(getComputedStyle(root).getPropertyValue('--mark-pad'));
      var pad = isNaN(padValue) ? 20 : padValue;
      var zoneHeight = ink.top + sink + ink.capHeight + pad;
      root.style.setProperty('--mark-h', px(zoneHeight));

      /* Only reserve vertical space for the letters where something is
         actually underneath them. On wide screens the columns sit far to
         the right of the mark, so they lift up to sit just below the rule
         instead of waiting for the letters to finish. */
      var cols = root.querySelector('.cols');
      var lift = 0;

      if (cols) {
        var footerRect = root.getBoundingClientRect();
        var markRight = markRect.right - footerRect.left;
        var colsLeft = cols.getBoundingClientRect().left - footerRect.left;
        var clearOfMark = colsLeft > markRight + 40;

        if (clearOfMark) {
          var solid = root.getAttribute('data-mark') === 'solid';
          var naturalTop = solid ? zoneHeight + 6 : zoneHeight - ink.seam + 19;
          var targetTop = solid ? ink.seam + 22 : 40;
          lift = Math.max(0, naturalTop - targetTop);
        }
      }

      root.style.setProperty('--body-lift', px(lift));
    } catch (e) {
      /* Leave whatever the CSS fallback already set. */
    }
  }

  /* Per-page override: a "Footer wordmark style" section in this page's
     template renders a hidden marker with the chosen placement. This script
     is deferred, so the swap happens after the DOM is parsed but before
     first paint: no flash of the wrong treatment. Without a marker, the
     footer's own server-rendered choice stands. */
  function applyOverride() {
    try {
      var marker = document.querySelector('[data-footer-mark]');
      if (!marker) return;
      var mark = marker.getAttribute('data-footer-mark');
      if (mark === 'split' || mark === 'solid') {
        root.setAttribute('data-mark', mark);
      }
      applyColor('--zf-mark-above', marker.getAttribute('data-footer-mark-above'));
      applyColor('--zf-mark-below', marker.getAttribute('data-footer-mark-below'));
    } catch (e) {
      /* keep the server-rendered treatment */
    }
  }

  /* An empty picker renders as "" (or "rgba(0,0,0,0)" when cleared in the
     editor); both mean "no override here", so the CSS falls back to the
     footer's own colours. */
  function applyColor(prop, value) {
    if (value && value !== 'rgba(0,0,0,0)') {
      root.style.setProperty(prop, value);
    } else {
      root.style.removeProperty(prop);
    }
  }

  function run() {
    applyOverride();
    measureMark();
  }

  var resizeTimer = null;
  function onResize() {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(run, 150);
  }

  applyOverride();

  try {
    if (document.fonts && document.fonts.ready && typeof document.fonts.ready.then === 'function') {
      document.fonts.ready.then(run, function () {});
    }
  } catch (e) {
    /* no document.fonts support; load/resize below still cover it */
  }

  if (document.readyState === 'complete') {
    run();
  } else {
    window.addEventListener('load', run);
  }

  window.addEventListener('resize', onResize);

  /* Theme editor live preview: re-apply when a section is added, removed,
     or its settings change, so the choice shows without a manual refresh. */
  document.addEventListener('shopify:section:load', run);
  document.addEventListener('shopify:section:unload', function () {
    try {
      if (!document.querySelector('[data-footer-mark]')) {
        root.setAttribute('data-mark', root.getAttribute('data-mark-default') || 'solid');
        root.style.removeProperty('--zf-mark-above');
        root.style.removeProperty('--zf-mark-below');
      }
      measureMark();
    } catch (e) { /* leave as is */ }
  });
})();
